import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { verifyDomain } from '@/api/domains'
import { AWS_ACCOUNTS } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ArrowLeft, Loader2 } from 'lucide-react'

const verifySchema = z.object({
  account_id: z.string().min(1, 'Please select an account'),
  domain: z
    .string()
    .min(1, 'Domain is required')
    .regex(
      /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
      'Please enter a valid domain name'
    ),
  configuration_set: z.string().optional(),
  mail_from_subdomain: z.string().optional(),
})

type VerifyFormData = z.infer<typeof verifySchema>

export function VerifyDomainForm() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<VerifyFormData>({
    resolver: zodResolver(verifySchema),
  })

  const onSubmit = async (data: VerifyFormData) => {
    setIsLoading(true)
    setError(null)

    const response = await verifyDomain({
      account_id: data.account_id,
      domain: data.domain,
      configuration_set: data.configuration_set || undefined,
      mail_from_subdomain: data.mail_from_subdomain || undefined,
    })

    setIsLoading(false)

    if (response.success) {
      // Redirect to domain detail page on success
      navigate(`/dashboard/domains/${encodeURIComponent(data.domain)}`)
    } else {
      setError(response.error || 'Failed to verify domain')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/dashboard/domains">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">Verify New Domain</h1>
          <p className="text-muted-foreground">
            Add a new domain to SES for email sending
          </p>
        </div>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>Domain Details</CardTitle>
          <CardDescription>
            Enter the domain information to start verification
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="account_id">AWS Account</Label>
              <Controller
                name="account_id"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account...">
                        {field.value && (
                          <span>
                            {AWS_ACCOUNTS.find(a => a.id === field.value)?.alias}
                            <span className="ml-2 text-muted-foreground font-mono text-xs">
                              {field.value}
                            </span>
                          </span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {AWS_ACCOUNTS.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{account.alias}</span>
                            <span className="text-xs text-muted-foreground font-mono">{account.id}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.account_id && (
                <p className="text-sm text-destructive">{errors.account_id.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="domain">Domain Name</Label>
              <Input
                id="domain"
                type="text"
                placeholder="example.com"
                {...register('domain')}
                disabled={isLoading}
              />
              {errors.domain && (
                <p className="text-sm text-destructive">{errors.domain.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="configuration_set">
                Configuration Set <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="configuration_set"
                type="text"
                placeholder="my-config-set"
                {...register('configuration_set')}
                disabled={isLoading}
              />
              {errors.configuration_set && (
                <p className="text-sm text-destructive">{errors.configuration_set.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mail_from_subdomain">
                Mail From Subdomain <span className="text-muted-foreground">(optional, default: mail)</span>
              </Label>
              <Input
                id="mail_from_subdomain"
                type="text"
                placeholder="mail"
                {...register('mail_from_subdomain')}
                disabled={isLoading}
              />
              {errors.mail_from_subdomain && (
                <p className="text-sm text-destructive">{errors.mail_from_subdomain.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Start Verification'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
